import type { SourceKey, RateOverrides } from "../../lib/revenuePlannerEngine";
import { SOURCE_LABELS, SOURCE_ICONS } from "../../lib/revenuePlannerEngine";
import type { FunnelSourceData } from "../../hooks/useFunnelHealth";
import { formatUSD, toDisplayCurrency } from "@/utils/formatters";

// =============================================================================
// SourceCard — Per-Source Funnel Breakdown (Used in Both Panels)
// =============================================================================
// Each source shows its own rich pipeline metrics instead of generic rates.
// - Webinar: Registered → Attended → Booked (D/U) → Shown → Closed → Cash
// - Typeform: Applied → Qualified/DQ → Booked (Q/R) → Shown → Closed → Cash
// - DMs: Leads → Booked → Pre-Calls → Shown → Closed → Cash
// =============================================================================

const SOURCE_COLORS: Record<SourceKey, string> = {
  webinar: "var(--funnel-webinar, #6366f1)",
  typeform: "var(--funnel-typeform, #f59e0b)",
  dm: "var(--funnel-colddm, #10b981)",
};

// --- Left Panel Variant: "What We Need" ---

interface ReverseCardProps {
  variant: "reverse";
  sourceKey: SourceKey;
  active: boolean;
  sourceGoal: number;
  sourceShare: number;
  neededLeadsPerDay: number;
  actualLeadsPerDay: number;
  gapLeadsPerDay: number;
  bookRate: number;
  showRate: number;
  closeRate: number;
  cashEarned: number;
  rawSourceData?: FunnelSourceData; // full pipeline data for source-specific metrics
}

// --- Right Panel Variant: "What Happens If" ---

interface DMEngagementMetrics {
  newDMChats: number;
  responseRate: number; // 0–100 (percent)
  realConvos: number;
  callsPitched: number; // booked from DM source in funnel data
}

interface ForwardCardProps {
  variant: "forward";
  sourceKey: SourceKey;
  active: boolean;
  leadsPerDay: number;
  onLeadsChange: (value: number) => void;
  onReset: () => void;
  isOverridden: boolean;
  projectedLeads: number;
  projectedAttended?: number; // webinar only
  projectedBooked: number;
  projectedShown: number;
  projectedClosed: number;
  projectedCash: number;
  // Rate overrides — editable conversion rates for scenario modeling
  rateOverrides?: RateOverrides;
  historicalRates?: {
    book_rate: number;
    show_rate: number;
    close_rate: number;
    aov: number;
    attendance_rate: number;
  };
  onRateOverride?: (field: keyof RateOverrides, value: number) => void;
  onRateOverrideClear?: (field: keyof RateOverrides) => void;
  // Webinar slider uses total registrants with a custom max
  webinarSliderMax?: number;
  dmAnalytics?: DMEngagementMetrics; // DM source only — engagement metrics for current month
}

type SourceCardProps = ReverseCardProps | ForwardCardProps;

function StatRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex justify-between items-center" style={{ padding: "3px 0" }}>
      <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{label}</span>
      <span
        className="font-semibold"
        style={{ fontSize: 12, color: color ?? "var(--text-primary)" }}
      >
        {value}
      </span>
    </div>
  );
}

function StatDivider() {
  return <div style={{ borderTop: "1px dashed var(--border-light)", margin: "6px 0" }} />;
}

/** Editable rate row — shows historical rate with an override input */
function EditableRateRow({
  label,
  historical,
  override,
  onOverride,
  onClear,
  isPercent = true,
}: {
  label: string;
  historical: number;
  override?: number;
  onOverride?: (value: number) => void;
  onClear?: () => void;
  isPercent?: boolean;
}) {
  const displayValue = override ?? historical;
  const isEdited = override !== undefined;

  if (!onOverride) {
    // Read-only
    return (
      <StatRow
        label={label}
        value={
          isPercent
            ? `${(displayValue * 100).toFixed(0)}%`
            : `$${Math.round(toDisplayCurrency(displayValue)).toLocaleString()}`
        }
        color={isEdited ? "var(--text-accent, var(--funnel-webinar, #6366f1))" : undefined}
      />
    );
  }

  return (
    <div className="flex justify-between items-center" style={{ padding: "3px 0" }}>
      <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{label}</span>
      <div className="flex items-center gap-1">
        {isEdited && (
          <button
            onClick={onClear}
            className="anim-tap"
            style={{
              fontSize: 9,
              color: "var(--text-tertiary)",
              background: "none",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            reset
          </button>
        )}
        <input
          type="number"
          min={0}
          max={isPercent ? 100 : 99999}
          step={isPercent ? 1 : 100}
          value={isPercent ? Math.round(displayValue * 100) : Math.round(displayValue)}
          onChange={(e) => {
            const raw = Number(e.target.value);
            onOverride(isPercent ? raw / 100 : raw);
          }}
          className="font-semibold rounded border text-center"
          style={{
            width: isPercent ? 44 : 68,
            padding: "2px 4px",
            fontSize: 11,
            color: isEdited
              ? "var(--text-accent, var(--funnel-webinar, #6366f1))"
              : "var(--text-primary)",
            background: "var(--surface)",
            borderColor: isEdited
              ? "var(--text-accent, var(--funnel-webinar, #6366f1))"
              : "var(--border)",
            outline: "none",
          }}
        />
        <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{isPercent ? "%" : ""}</span>
      </div>
    </div>
  );
}

// --- Source-Specific Pipeline Metrics (replaces generic Book/Show/Close Rate) ---

function WebinarPipeline({
  data,
  showRate,
  closeRate,
}: {
  data: FunnelSourceData;
  showRate: number;
  closeRate: number;
}) {
  const attendanceRate =
    (data.total_signups ?? 0) > 0 ? ((data.attended ?? 0) / (data.total_signups ?? 1)) * 100 : 0;

  return (
    <>
      <StatRow label="Registered" value={(data.total_signups ?? 0).toLocaleString()} />
      <StatRow label="Attended" value={(data.attended ?? 0).toLocaleString()} />
      <StatRow label="Missed" value={(data.missed ?? 0).toLocaleString()} />
      <StatRow label="Attendance Rate" value={`${attendanceRate.toFixed(0)}%`} />
      <StatDivider />
      <StatRow label="Booked" value={data.booked.toLocaleString()} />
      {(data.webinar_discovery_booked ?? 0) > 0 && (
        <StatRow
          label="  Discovery"
          value={(data.webinar_discovery_booked ?? 0).toLocaleString()}
        />
      )}
      {(data.webinar_upsell_booked ?? 0) > 0 && (
        <StatRow label="  Upsell" value={(data.webinar_upsell_booked ?? 0).toLocaleString()} />
      )}
      <StatRow label="Shown" value={data.shown.toLocaleString()} />
      <StatRow label="Show Rate" value={`${(showRate * 100).toFixed(0)}%`} />
      <StatRow label="Closed" value={data.closed.toLocaleString()} />
      <StatRow label="Close Rate" value={`${(closeRate * 100).toFixed(0)}%`} />
    </>
  );
}

function TypeformPipeline({
  data,
  showRate,
  closeRate,
}: {
  data: FunnelSourceData;
  showRate: number;
  closeRate: number;
}) {
  return (
    <>
      <StatRow label="Applied" value={data.leads_in.toLocaleString()} />
      <StatRow label="Qualified" value={(data.qualified ?? 0).toLocaleString()} />
      <StatRow label="DQ'd" value={(data.dq ?? 0).toLocaleString()} />
      <StatRow label="DQ Rate" value={`${(data.dq_rate ?? 0).toFixed(0)}%`} />
      <StatDivider />
      <StatRow label="Booked" value={data.booked.toLocaleString()} />
      {(data.typeform_qual_booked ?? 0) > 0 && (
        <StatRow
          label="  Qualified Path"
          value={(data.typeform_qual_booked ?? 0).toLocaleString()}
        />
      )}
      {(data.typeform_revived_booked ?? 0) > 0 && (
        <StatRow
          label="  Revived DQ"
          value={(data.typeform_revived_booked ?? 0).toLocaleString()}
        />
      )}
      <StatRow label="Shown" value={data.shown.toLocaleString()} />
      <StatRow label="Show Rate" value={`${(showRate * 100).toFixed(0)}%`} />
      <StatRow label="Closed" value={data.closed.toLocaleString()} />
      <StatRow label="Close Rate" value={`${(closeRate * 100).toFixed(0)}%`} />
    </>
  );
}

function DMPipeline({
  data,
  bookRate,
  showRate,
  closeRate,
  dmAnalytics,
}: {
  data: FunnelSourceData;
  bookRate: number;
  showRate: number;
  closeRate: number;
  dmAnalytics?: DMEngagementMetrics;
}) {
  return (
    <>
      {dmAnalytics && (
        <>
          <div
            style={{
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "var(--text-tertiary)",
              marginBottom: 4,
            }}
          >
            DM Engagement
          </div>
          <StatRow label="New Chats" value={dmAnalytics.newDMChats.toLocaleString()} />
          <StatRow label="Response Rate" value={`${dmAnalytics.responseRate.toFixed(0)}%`} />
          <StatRow label="Conversations" value={dmAnalytics.realConvos.toLocaleString()} />
          <StatRow label="Calls Pitched" value={dmAnalytics.callsPitched.toLocaleString()} />
          <StatDivider />
        </>
      )}
      <StatRow label="DM Leads" value={data.leads_in.toLocaleString()} />
      <StatRow label="Booked" value={data.booked.toLocaleString()} />
      <StatRow label="Book Rate" value={`${(bookRate * 100).toFixed(0)}%`} />
      <StatRow label="Pre-Calls Sent" value={(data.pre_calls_filled ?? 0).toLocaleString()} />
      <StatRow label="Cancellations" value={(data.cancellations ?? 0).toLocaleString()} />
      <StatDivider />
      <StatRow label="Shown" value={data.shown.toLocaleString()} />
      <StatRow label="Show Rate" value={`${(showRate * 100).toFixed(0)}%`} />
      <StatRow label="Closed" value={data.closed.toLocaleString()} />
      <StatRow label="Close Rate" value={`${(closeRate * 100).toFixed(0)}%`} />
    </>
  );
}

export default function SourceCard(props: SourceCardProps) {
  const color = SOURCE_COLORS[props.sourceKey];

  if (!props.active) {
    return (
      <div
        className="rounded-xl flex items-center"
        style={{
          background: "var(--bg)",
          border: "1px solid var(--border)",
          padding: "12px 16px",
          opacity: 0.4,
        }}
      >
        <span style={{ fontSize: 14 }}>{SOURCE_ICONS[props.sourceKey]}</span>
        <span className="font-medium ml-2" style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          {SOURCE_LABELS[props.sourceKey]}
        </span>
        <span
          className="ml-auto font-medium"
          style={{ fontSize: 10, color: "var(--text-tertiary)" }}
        >
          OFF
        </span>
      </div>
    );
  }

  if (props.variant === "reverse") {
    const {
      sourceKey,
      sourceGoal,
      sourceShare,
      neededLeadsPerDay,
      actualLeadsPerDay,
      gapLeadsPerDay,
      bookRate,
      showRate,
      closeRate,
      cashEarned,
      rawSourceData,
    } = props;
    const gapColor = gapLeadsPerDay > 0 ? "var(--red, #dc2626)" : "var(--green, #16a34a)";

    return (
      <div className="liquid-glass anim-lift rounded-xl" style={{ padding: "16px" }}>
        {/* Header */}
        <div className="flex items-center gap-2 mb-3">
          <span style={{ fontSize: 14 }}>{SOURCE_ICONS[sourceKey]}</span>
          <span className="font-semibold" style={{ fontSize: 12, color: "var(--text-primary)" }}>
            {SOURCE_LABELS[sourceKey]}
          </span>
          <span
            className="ml-auto font-medium rounded"
            style={{
              fontSize: 9,
              color,
              background: `color-mix(in srgb, ${color} 8%, transparent)`,
              padding: "2px 8px",
            }}
          >
            {(sourceShare * 100).toFixed(0)}% share
          </span>
        </div>

        {/* Leads/day comparison */}
        <div className="rounded-lg mb-3" style={{ padding: "10px 12px", background: "var(--bg)" }}>
          <div className="flex justify-between items-baseline">
            <div>
              <div style={{ fontSize: 9, color: "var(--text-tertiary)", marginBottom: 2 }}>
                Need
              </div>
              <span className="font-bold" style={{ fontSize: 18, color: "var(--text-primary)" }}>
                {neededLeadsPerDay}
              </span>
              <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                {sourceKey === "webinar" ? " signups" : " leads/day"}
              </span>
            </div>
            <div className="text-right">
              <div style={{ fontSize: 9, color: "var(--text-tertiary)", marginBottom: 2 }}>
                Have
              </div>
              <span className="font-bold" style={{ fontSize: 18, color: "var(--text-secondary)" }}>
                {actualLeadsPerDay}
              </span>
              <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>
                {sourceKey === "webinar" ? " signups" : " leads/day"}
              </span>
            </div>
          </div>
          {gapLeadsPerDay !== 0 && (
            <div className="text-right mt-1 font-medium" style={{ fontSize: 10, color: gapColor }}>
              {gapLeadsPerDay > 0 ? "+" : ""}
              {gapLeadsPerDay} gap
            </div>
          )}
        </div>

        {/* Source-specific pipeline metrics */}
        {rawSourceData ? (
          sourceKey === "webinar" ? (
            <WebinarPipeline data={rawSourceData} showRate={showRate} closeRate={closeRate} />
          ) : sourceKey === "typeform" ? (
            <TypeformPipeline data={rawSourceData} showRate={showRate} closeRate={closeRate} />
          ) : (
            <DMPipeline
              data={rawSourceData}
              bookRate={bookRate}
              showRate={showRate}
              closeRate={closeRate}
            />
          )
        ) : (
          // Fallback: generic rates if raw data not available
          <>
            <StatRow label="Book Rate" value={`${(bookRate * 100).toFixed(0)}%`} />
            <StatRow label="Show Rate" value={`${(showRate * 100).toFixed(0)}%`} />
            <StatRow label="Close Rate" value={`${(closeRate * 100).toFixed(0)}%`} />
          </>
        )}

        {/* Earned + goal */}
        <div className="mt-3 pt-3 space-y-1" style={{ borderTop: "1px solid var(--border-light)" }}>
          <StatRow label="Earned so far" value={formatUSD(cashEarned)} />
          <StatRow label="Goal from this source" value={formatUSD(sourceGoal)} color={color} />
        </div>
      </div>
    );
  }

  // Forward variant
  const {
    leadsPerDay,
    onLeadsChange,
    onReset,
    isOverridden,
    projectedLeads,
    projectedAttended,
    projectedBooked,
    projectedShown,
    projectedClosed,
    projectedCash,
    rateOverrides,
    historicalRates,
    onRateOverride,
    onRateOverrideClear,
    webinarSliderMax,
    dmAnalytics,
  } = props;

  const isWebinar = props.sourceKey === "webinar";

  return (
    <div className="liquid-glass anim-lift rounded-xl" style={{ padding: "16px" }}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <span style={{ fontSize: 14 }}>{SOURCE_ICONS[props.sourceKey]}</span>
        <span className="font-semibold" style={{ fontSize: 12, color: "var(--text-primary)" }}>
          {SOURCE_LABELS[props.sourceKey]}
        </span>
        {isOverridden && (
          <button
            onClick={onReset}
            className="anim-tap ml-auto font-medium"
            style={{
              fontSize: 9,
              color: "var(--text-tertiary)",
              background: "none",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Reset
          </button>
        )}
      </div>

      {/* Slider + input — drag to model scenarios */}
      <p style={{ fontSize: 10, color: "var(--text-tertiary)", marginBottom: 6, lineHeight: 1.4 }}>
        {isWebinar
          ? "Set total registrants to project the full webinar funnel"
          : `Drag to see what happens if you get more or fewer ${SOURCE_LABELS[props.sourceKey].toLowerCase()} leads per day`}
      </p>
      <div className="rounded-lg mb-3" style={{ padding: "10px 12px", background: "var(--bg)" }}>
        <div style={{ fontSize: 9, color: "var(--text-tertiary)", marginBottom: 4 }}>
          {isWebinar ? "Total Registrants" : "Leads / day"}
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={0}
            max={
              isWebinar
                ? (webinarSliderMax ?? Math.max(500, Math.ceil(leadsPerDay * 3)))
                : Math.min(500, Math.max(200, Math.ceil(leadsPerDay * 3)))
            }
            step={isWebinar ? 10 : 1}
            value={leadsPerDay}
            onChange={(e) => onLeadsChange(Number(e.target.value))}
            className="flex-1"
            style={{
              accentColor: color,
              height: 4,
            }}
          />
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={isWebinar ? (webinarSliderMax ?? 9999) : 500}
              value={Math.round(leadsPerDay)}
              onChange={(e) =>
                onLeadsChange(
                  Math.min(isWebinar ? (webinarSliderMax ?? 9999) : 500, Number(e.target.value))
                )
              }
              className="font-semibold rounded border text-center"
              style={{
                width: isWebinar ? 64 : 52,
                padding: "4px",
                fontSize: 13,
                color: "var(--text-primary)",
                background: "var(--surface)",
                borderColor: "var(--border)",
                outline: "none",
              }}
            />
            {!isWebinar && (
              <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>/day</span>
            )}
          </div>
        </div>
      </div>

      {/* DM engagement metrics — only shown for DM source when analytics available */}
      {props.sourceKey === "dm" && dmAnalytics && (
        <div className="mb-3">
          <div
            style={{
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "var(--text-tertiary)",
              marginBottom: 4,
            }}
          >
            DM Engagement
          </div>
          <StatRow label="New Chats" value={dmAnalytics.newDMChats.toLocaleString()} />
          <StatRow label="Response Rate" value={`${dmAnalytics.responseRate.toFixed(0)}%`} />
          <StatRow label="Conversations" value={dmAnalytics.realConvos.toLocaleString()} />
          <StatRow label="Calls Pitched" value={dmAnalytics.callsPitched.toLocaleString()} />
          <StatDivider />
        </div>
      )}

      {/* Projection chain */}
      {isWebinar ? (
        <>
          <StatRow label="Registrants" value={projectedLeads.toLocaleString()} />
          {historicalRates && (
            <EditableRateRow
              label="Attendance Rate"
              historical={historicalRates.attendance_rate}
              override={rateOverrides?.attendance_rate}
              onOverride={onRateOverride ? (v) => onRateOverride("attendance_rate", v) : undefined}
              onClear={
                onRateOverrideClear ? () => onRateOverrideClear("attendance_rate") : undefined
              }
            />
          )}
          <StatRow label="Attended" value={(projectedAttended ?? 0).toLocaleString()} />
          {historicalRates && (
            <EditableRateRow
              label="Book Rate"
              historical={historicalRates.book_rate}
              override={rateOverrides?.book_rate}
              onOverride={onRateOverride ? (v) => onRateOverride("book_rate", v) : undefined}
              onClear={onRateOverrideClear ? () => onRateOverrideClear("book_rate") : undefined}
            />
          )}
          <StatRow label="Booked" value={projectedBooked.toLocaleString()} />
          {historicalRates && (
            <EditableRateRow
              label="Show Rate"
              historical={historicalRates.show_rate}
              override={rateOverrides?.show_rate}
              onOverride={onRateOverride ? (v) => onRateOverride("show_rate", v) : undefined}
              onClear={onRateOverrideClear ? () => onRateOverrideClear("show_rate") : undefined}
            />
          )}
          <StatRow label="Shown" value={projectedShown.toLocaleString()} />
          {historicalRates && (
            <EditableRateRow
              label="Close Rate"
              historical={historicalRates.close_rate}
              override={rateOverrides?.close_rate}
              onOverride={onRateOverride ? (v) => onRateOverride("close_rate", v) : undefined}
              onClear={onRateOverrideClear ? () => onRateOverrideClear("close_rate") : undefined}
            />
          )}
          <StatRow label="Closed" value={projectedClosed.toLocaleString()} />
          {historicalRates && (
            <EditableRateRow
              label="AOV"
              historical={historicalRates.aov}
              override={rateOverrides?.aov}
              onOverride={onRateOverride ? (v) => onRateOverride("aov", v) : undefined}
              onClear={onRateOverrideClear ? () => onRateOverrideClear("aov") : undefined}
              isPercent={false}
            />
          )}
        </>
      ) : (
        <>
          <StatRow label="Leads" value={projectedLeads.toLocaleString()} />
          <StatRow label="Booked" value={projectedBooked.toLocaleString()} />
          <StatRow label="Shown" value={projectedShown.toLocaleString()} />
          <StatRow label="Closed" value={projectedClosed.toLocaleString()} />
        </>
      )}

      {/* Projected cash by EOM from this source */}
      <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border-light)" }}>
        <StatRow label="Projected Cash by EOM" value={formatUSD(projectedCash)} color={color} />
      </div>
    </div>
  );
}
